import React from "react"
import useSWR from "swr"
import { OrderApi, SavedListApi } from "../../../apis"
import type { IMatchRow } from "../../../apis/saved-list"
import { flattenCatalogue, useCatalogue, type CatalogueProduct } from "../product-list/catalogue-data"
import { sameCode } from "../quick-order/parse"
import { orderLinesToCart, orderSupplier } from "../reorder"

/** What the right panel re-orders: a saved list of the chosen supplier, or a past order. */
export type ReorderSource = { kind: "saved"; id: string; name: string; date: string } | { kind: "order"; id: string; name: string; date: string }

export type LineStatus = "OK" | "PRICE_CHANGED" | "LOW_STOCK" | "OUT" | "NO_PRICE" | "NOT_FOUND"

export type CheckedLine = {
  variantId: string
  parentVariantId?: string | null
  name?: string
  skuCode?: string
  image?: string
  quantity: number
  previousPrice?: number
  currentPrice: number
  available?: number
  status: LineStatus
  /** Quantity that goes into the cart (stock on LOW_STOCK). */
  addQty: number
  selectable: boolean
  product?: CatalogueProduct
  /** A past order line's own warehouse, used when the variant is not in the catalogue. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inventories?: any[]
}

type BaseLine = Omit<CheckedLine, "currentPrice" | "available" | "status" | "addQty" | "selectable">

function check(line: BaseLine, m: IMatchRow | undefined): CheckedLine {
  const currentPrice = Number(m?.price) || 0
  const available = m?.available
  const base = { ...line, currentPrice, available, addQty: 0, selectable: false }
  if (!m || m.status === "NOT_FOUND") return { ...base, status: "NOT_FOUND" }
  if (available === 0) return { ...base, status: "OUT" }
  if (currentPrice <= 0) return { ...base, status: "NO_PRICE" }
  if (m.status === "LOW_STOCK") {
    const addQty = Math.max(0, Number(m.addQuantity ?? available) || 0)
    return { ...base, status: "LOW_STOCK", addQty, selectable: addQty > 0 }
  }
  const changed = line.previousPrice != null && line.previousPrice > 0 && line.previousPrice !== currentPrice
  return { ...base, status: changed ? "PRICE_CHANGED" : "OK", addQty: line.quantity, selectable: true }
}

/**
 * The source's lines with today's price and stock: a saved list's variants are
 * looked up in the supplier catalogue for their SKU, a past order carries its own;
 * both are then re-checked through `POST /ord/quick-order/match` by SKU.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useReorderLines(source: ReorderSource | null, listSupplier: any) {
  const detail = useSWR(
    source ? ["reorder-source", source.kind, source.id] : null,
    async () => {
      if (source!.kind === "saved") return { saved: await SavedListApi.get(source!.id), order: null }
      return { saved: null, order: await OrderApi.get(source!.id) }
    },
    { revalidateOnFocus: false },
  )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const supplier: any = source?.kind === "order" ? orderSupplier(detail.data?.order) || null : listSupplier
  const catalogueSuppliers = React.useMemo(() => (supplier?.id ? [supplier] : []), [supplier])
  const catalogue = useCatalogue(catalogueSuppliers)
  const products = React.useMemo(() => flattenCatalogue(catalogue.data), [catalogue.data])

  const baseLines = React.useMemo<BaseLine[]>(() => {
    const data = detail.data
    if (!data) return []
    if (data.saved) {
      return (data.saved.lines || []).map((l) => {
        const product = products.find((p) => p.id === l.variantId)
        return {
          variantId: l.variantId,
          parentVariantId: l.parentVariantId,
          name: product?.nameMon,
          skuCode: product?.skuCode,
          image: product?.image,
          quantity: l.quantity,
          product,
        }
      })
    }
    return orderLinesToCart(data.order).map((c) => ({
      variantId: c.variantId,
      parentVariantId: c.variantParentId,
      name: c.nameMon,
      skuCode: c.skuCode,
      image: c.image,
      quantity: c.quantity,
      previousPrice: c.price,
      inventories: c.inventories,
      product: products.find((p) => p.id === c.variantId),
    }))
  }, [detail.data, products])

  // A saved list needs the catalogue for its SKUs; wait for it before matching.
  const waitingCatalogue = !!detail.data?.saved && catalogue.isLoading
  const codes = React.useMemo(
    () => baseLines.filter((l) => !!l.skuCode).map((l) => ({ code: String(l.skuCode), quantity: l.quantity })),
    [baseLines],
  )
  const matched = useSWR(
    supplier?.id && codes.length > 0 && !waitingCatalogue ? ["reorder-match", supplier.id, JSON.stringify(codes)] : null,
    () => SavedListApi.match({ supplierBusinessId: supplier.id, rows: codes }),
    { revalidateOnFocus: false },
  )

  const lines = React.useMemo(
    () => baseLines.map((l) => check(l, l.skuCode ? (matched.data || []).find((m) => sameCode(m.code, String(l.skuCode))) : undefined)),
    [baseLines, matched.data],
  )

  return {
    lines,
    supplier,
    isLoading: detail.isLoading || waitingCatalogue || matched.isLoading,
    error: detail.error || matched.error,
    retry: () => {
      detail.mutate()
      matched.mutate()
    },
  }
}
